import React, {useState, useCallback, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {createFragmentContainer, graphql} from 'react-relay';
import {withRouter} from 'react-router-dom';
import {TopNavigation} from '@forecast-it/design-system';
import AIChatPanel, {DEFAULT_WIDTH as CHAT_DEFAULT_WIDTH} from '../../../components/ai-chat/AIChatPanel';
import CreateButton, {CreateButtonQuery, DropDownButton as CreateDropDownButton} from '../header/CreateButton';
import ProfileButton from '../header/ProfileButton';
import HelpButton from '../header/HelpButton';
import GlobalSearchV2 from '../header/global-search/GlobalSearchButton';
import Util from '../../shared/util/util';
import Timer, {TimerLoader, TimerQuery} from '../header/Timer';
import {withSocketHandling} from '../../../socket/withSocketHandling';
import {ForecastQueryRenderer} from '../../../ForecastQueryRenderer';
import {getProjectIdFromPath} from '../../shared/util/UrlContextUtil';
import RecentButton, {DropDownButton as RecentDropdownButton, RecentButtonQuery} from '../header/RecentButton';
import KeyboardShortcuts from '../header/KeyboardShortcuts';
import {useIntl} from 'react-intl';
import {hasPermission, isClientUser} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasFeatureFlag} from '../../shared/util/FeatureUtil';
import NotificationMenu from '../header/NotificationMenu';
import {HIDDEN_FEATURES} from '../../../constants';
import {isFeatureHidden} from '../../shared/util/CompanySetupUtil';
import CreateApiKeyMutation from '../../../mutations/create_api_key_mutation';

const AI_CHAT_OPEN_KEY = 'ai-chat-open';

const PrimaryNavigationRight = props => {
	const {viewer} = props;
	const {actualPersonId} = props;
	const [timerHidden, setTimerHidden] = useState(
		localStorage.getItem('header-hide-timer') ? JSON.parse(localStorage.getItem('header-hide-timer')) : false
	);
	const [chatOpen, setChatOpen] = useState(() => {
		try {
			return sessionStorage.getItem(AI_CHAT_OPEN_KEY) === 'true';
		} catch (_) {
			return false;
		}
	});
	const [provisioningState, setProvisioningState] = useState('idle'); // 'idle' | 'provisioning' | 'provisioned' | 'failed'
	useEffect(() => {
		try {
			sessionStorage.setItem(AI_CHAT_OPEN_KEY, String(chatOpen));
		} catch (_) {}
	}, [chatOpen]);
	const currentPath = window.location.pathname + window.location.hash;
	const {formatMessage} = useIntl();

	// Resolve the first active API key from the viewer's company API keys.
	// Falls back to an empty string if none are found (panel will show a banner).
	const activeApiKey = (() => {
		const edges = viewer.company.apiKeys ? viewer.company.apiKeys.edges : [];
		const active = edges.find(e => e.node && e.node.active);
		return active ? active.node.key : '';
	})();

	// Auto-provision an API key for admin users on first open if none exists.
	useEffect(() => {
		if (chatOpen && !activeApiKey && provisioningState === 'idle') {
			setProvisioningState('provisioning');
			CreateApiKeyMutation.commit(
				props.relay.environment,
				viewer.company.id,
				() => setProvisioningState('provisioned'),
				() => setProvisioningState('failed')
			);
		}
	}, [chatOpen, activeApiKey, provisioningState, props.relay.environment, viewer.company.id]);

	return (
		<TopNavigation.Right>
			<KeyboardShortcuts key={'KeyboardShortcuts'} actualPersonId={actualPersonId} />

			{!timerHidden && !isClientUser() && !isFeatureHidden(HIDDEN_FEATURES.TIME_REGISTRATIONS) ? (
				<ForecastQueryRenderer
					key="query-render-timer_component"
					query={TimerQuery}
					customLoader={() => TimerLoader}
					render={relayProps => <Timer {...relayProps} personId={actualPersonId} />}
				/>
			) : null}

			<ForecastQueryRenderer
				key="query-render-create_button"
				query={CreateButtonQuery}
				variables={{
					projectId: getProjectIdFromPath(currentPath),
					companyId: viewer.company.id,
				}}
				customLoader={() => CreateDropDownButton(formatMessage)}
				render={relayProps => <CreateButton {...relayProps} />}
			/>

			<TopNavigation.Controls>
				<GlobalSearchV2 companyId={viewer.company.id} personId={viewer.backendId} />

				<ForecastQueryRenderer
					key="query-render-top_navigation-favorites"
					query={RecentButtonQuery}
					variables={{}}
					redirectIfNotLoggedIn={true}
					customLoader={() => RecentDropdownButton}
					render={(relayProps, retry) => <RecentButton retry={retry} {...relayProps} />}
				/>
				<NotificationMenu viewer={viewer} />
				<HelpButton />
				<ProfileButton
					viewer={viewer}
					timerHidden={timerHidden}
					onToggleTimer={() => {
						Util.localStorageSetItem('header-hide-timer', !timerHidden);
						setTimerHidden(!timerHidden);
					}}
				/>
			</TopNavigation.Controls>

			{hasFeatureFlag('forecast_agent_chat') &&
				hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS) &&
				ReactDOM.createPortal(
					<AIChatFab
						open={chatOpen}
						onToggle={() => setChatOpen(open => !open)}
						apiKey={activeApiKey}
						viewerPersonId={viewer.backendId}
						history={props.history}
						onClose={() => setChatOpen(false)}
						isProvisioning={provisioningState === 'idle' || provisioningState === 'provisioning'}
					/>,
					document.body
				)}
		</TopNavigation.Right>
	);
};

const PrimaryNavigationRightQuery = graphql`
	query PrimaryNavigationRight_Query {
		viewer {
			actualPersonId
			component(name: "top_navigation_right")
			...PrimaryNavigationRight_viewer
		}
	}
`;

// ── Floating AI Agent button + panel ──────────────────────────────────────────

// Duration must match the ai-card-out animation in AIChatPanel.js
const CLOSE_ANIMATION_MS = 250;

function AIChatFab({open, onToggle, apiKey, viewerPersonId, history, onClose, isProvisioning}) {
	const [panelWidth, setPanelWidth] = useState(CHAT_DEFAULT_WIDTH);
	const [closing, setClosing] = useState(false);

	// Stable callback — prevents AIChatPanel's useCallback from re-running on every render
	const handleWidthChange = useCallback(w => setPanelWidth(w), []);

	// Trigger collapse animation, then fully close after it finishes
	const handleClose = useCallback(() => {
		setClosing(true);
		setTimeout(() => {
			onClose();
			setClosing(false);
		}, CLOSE_ANIMATION_MS);
	}, [onClose]);

	const fabOpenStyles = {
		background: '#ffffff',
		color: '#2e2541',
		cursor: 'default',
		pointerEvents: 'none',
		userSelect: 'none',
		boxShadow: '0 0 0 2px rgba(110,15,235,0.3), 0 4px 20px rgba(57,38,100,0.18)',
	};

	const fabClosedStyles = {
		background: 'linear-gradient(135deg, #392664, #6E0FEB)',
		color: '#ffffff',
		cursor: 'pointer',
		pointerEvents: 'auto',
		userSelect: 'none',
		boxShadow: '0 4px 16px rgba(57,38,100,0.35)',
	};

	return (
		<>
			{/* FAB — always visible; transforms to white status bar when panel is open */}
			<button
				onClick={open ? undefined : onToggle}
				aria-label="Forecast Agent"
				title="Forecast Agent"
				style={{
					position: 'fixed',
					bottom: 24,
					right: 84,
					zIndex: 300,
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					padding: '0 18px 0 12px',
					height: 44,
					width: panelWidth,
					border: 'none',
					borderRadius: 22,
					fontFamily: "'neue-haas-grotesk-text', 'Inter', sans-serif",
					fontSize: 13,
					fontWeight: 600,
					letterSpacing: '0.1px',
					whiteSpace: 'nowrap',
					transition: 'background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease',
					...(open ? fabOpenStyles : fabClosedStyles),
				}}
				onMouseEnter={e => {
					if (!open) e.currentTarget.style.boxShadow = '0 6px 20px rgba(57,38,100,0.55)';
				}}
				onMouseLeave={e => {
					if (!open) e.currentTarget.style.boxShadow = '0 4px 16px rgba(57,38,100,0.35)';
				}}
			>
				<svg width="18" height="18" viewBox="0 0 18 18" fill="none" style={{flexShrink: 0}}>
					<path
						d="M9 2l1.4 4.1H15l-3.3 2.4 1.3 4L9 10.4l-3.9 2.1 1.3-4L3 6.1h4.6L9 2z"
						fill={open ? '#392664' : '#ffffff'}
						style={{transition: 'fill 0.3s ease'}}
					/>
				</svg>
				<span style={{flex: 1}}>Forecast Agent</span>
				{open && (
					<span
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: '#22c55e',
							flexShrink: 0,
							boxShadow: '0 0 4px rgba(34,197,94,0.6)',
							animation: 'fab-dot-pulse 2s ease-in-out infinite',
						}}
					/>
				)}
			</button>

			{/* Keyframe for the green active dot */}
			<style>{`
				@keyframes fab-dot-pulse {
					0%, 100% { opacity: 1; box-shadow: 0 0 4px rgba(34,197,94,0.6); }
					50% { opacity: 0.7; box-shadow: 0 0 8px rgba(34,197,94,0.9); }
				}
			`}</style>

			{/* Panel — mounted while open OR while the close animation is playing */}
			{(open || closing) && (
				<AIChatPanel
					apiKey={apiKey}
					viewerPersonId={viewerPersonId}
					onClose={handleClose}
					history={history}
					onWidthChange={handleWidthChange}
					isClosing={closing}
					isProvisioning={isProvisioning}
				/>
			)}
		</>
	);
}

export {PrimaryNavigationRightQuery};

export default withRouter(
	withSocketHandling(
		createFragmentContainer(PrimaryNavigationRight, {
			viewer: graphql`
				fragment PrimaryNavigationRight_viewer on Viewer {
					id
					sisenseUserId
					backendId
					unseenNotifications
					projectOwner
					company {
						id
						useTimeApproval
						useTimeOffApproval
						unit4Enabled
						isUsingProjectAllocation
						tier
						apiKeys(first: 10) @connection(key: "PrimaryNavigationRightCompany_apiKeys") {
							edges {
								node {
									key
									active
								}
							}
						}
					}
					...NotificationMenu_viewer
				}
			`,
		})
	)
);
